import authService from './authService';

const bedrockService = {
  // If any API call returns 401/403, clear tokens and redirect to login immediately
  _handleUnauthorized(response) {
    if (response.status === 401 || response.status === 403) {
      authService.clearTokens();
      window.location.href = '/';
      throw new Error('Session expired');
    }
  },

  async getUserModels() {
    const { API_GATEWAY_URL, DEV_MODE } = window.CONFIG;
    const token = authService.getToken();

    const headers = {
      'Content-Type': 'application/json'
    };

    if (!DEV_MODE) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_GATEWAY_URL}/models`, {
      method: 'GET',
      headers: headers
    });

    if (!response.ok) {
      this._handleUnauthorized(response);
      throw new Error(`Failed to get user models: ${response.status}`);
    }

    return await response.json();
  },

  async sendMessageStream(modelId, messages, onChunk, skipGuardrail = false, systemContext = null, signal = null) {
    const { API_GATEWAY_URL, DEV_MODE } = window.CONFIG;
    const token = authService.getToken();

    const validMessages = messages.filter(msg => 
      msg.content && 
      msg.content.length > 0 &&
      msg.content.some(item => 
        (item.text && item.text.trim() !== '') || 
        item.document ||
        item.image
      )
    );

    const headers = {
      'Content-Type': 'application/json',
      'aws-endpoint-prefix': 'bedrock-runtime'
    };

    if (!DEV_MODE) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const encodedModelId = encodeURIComponent(modelId);
    const url = skipGuardrail
      ? `${API_GATEWAY_URL}/model/${encodedModelId}/converse-stream?skipGuardrail=true`
      : `${API_GATEWAY_URL}/model/${encodedModelId}/converse-stream`;
    
    const requestBody = { 
      messages: validMessages,
      modelId: modelId
    };

    if (systemContext) {
      requestBody.system = systemContext;
      console.log('Sending system context with request:', JSON.stringify(systemContext).substring(0, 200));
    } else {
      console.log('No system context — memoryRecords not available');
    }

    const fetchOptions = {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody)
    };
    if (signal) {
      fetchOptions.signal = signal;
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      this._handleUnauthorized(response);
      throw new Error(`API error: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          
          try {
            const parsed = JSON.parse(data);
            if (parsed.contentBlockDelta?.delta?.text) {
              onChunk(parsed.contentBlockDelta.delta.text);
            }
          } catch (e) {
            console.warn('Failed to parse chunk:', e);
          }
        }
      }
    }
  },

  async listEvents(actorId, sessionId = null) {
    const { API_GATEWAY_URL, DEV_MODE } = window.CONFIG;
    const token = authService.getToken();

    const headers = { 'Content-Type': 'application/json' };
    if (!DEV_MODE) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const parameters = {
      actor_id: actorId,
      include_payload: true,
      max_results: 100
    };

    if (sessionId) {
      parameters.session_id = sessionId;
    }

    const response = await fetch(`${API_GATEWAY_URL}/conversationhistory`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        operation: 'list_events',
        parameters: parameters
      })
    });

    if (!response.ok) {
      this._handleUnauthorized(response);
      throw new Error(`Failed to list events: ${response.status}`);
    }

    return await response.json();
  },

  async addEvent(actorId, sessionId, messages, metadata = null) {
    const { API_GATEWAY_URL, DEV_MODE } = window.CONFIG;
    const token = authService.getToken();

    const headers = { 'Content-Type': 'application/json' };
    if (!DEV_MODE) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const parameters = {
      actor_id: actorId,
      session_id: sessionId,
      messages: messages
    };

    if (metadata) {
      parameters.metadata = metadata;
    }

    const response = await fetch(`${API_GATEWAY_URL}/conversationhistory`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        operation: 'add_event',
        parameters: parameters
      })
    });

    if (!response.ok) {
      this._handleUnauthorized(response);
      throw new Error(`Failed to add event: ${response.status}`);
    }

    return await response.json();
  },

  async pinSession(actorId, sessionId, pinned = true, pinOrder = 0) {
    const { API_GATEWAY_URL, DEV_MODE } = window.CONFIG;
    const token = authService.getToken();

    const headers = { 'Content-Type': 'application/json' };
    if (!DEV_MODE) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_GATEWAY_URL}/conversationhistory`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        operation: 'add_event',
        parameters: {
          actor_id: actorId,
          session_id: sessionId,
          messages: [['Session pinned status updated', 'OTHER']],
          metadata: {
            pinned: { stringValue: pinned ? 'true' : 'false' },
            pinOrder: { stringValue: String(pinOrder) },
            action: { stringValue: 'pin_status_update' }
          }
        }
      })
    });

    if (!response.ok) {
      this._handleUnauthorized(response);
      throw new Error(`Failed to pin session: ${response.status}`);
    }

    return await response.json();
  },

  async listSessions(userId, maxResults = 10, nextToken = null) {
    const { API_GATEWAY_URL, DEV_MODE } = window.CONFIG;
    const token = authService.getToken();

    const headers = { 'Content-Type': 'application/json' };
    if (!DEV_MODE) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const parameters = {
      actor_id: userId,
      max_results: maxResults
    };

    if (nextToken) {
      parameters.next_token = nextToken;
    }

    const response = await fetch(`${API_GATEWAY_URL}/conversationhistory`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        operation: 'list_sessions',
        parameters: parameters
      })
    });

    if (!response.ok) {
      this._handleUnauthorized(response);
      throw new Error(`Failed to list sessions: ${response.status}`);
    }

    return await response.json();
  },

  async listMemoryRecords(userId) {
    const { API_GATEWAY_URL, DEV_MODE } = window.CONFIG;
    const token = authService.getToken();

    const headers = { 'Content-Type': 'application/json' };
    if (!DEV_MODE) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_GATEWAY_URL}/conversationhistory`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        operation: 'list_memory_records',
        parameters: {
          actor_id: userId,
          max_results: 100
        }
      })
    });

    if (!response.ok) {
      this._handleUnauthorized(response);
      throw new Error(`Failed to list memory records: ${response.status}`);
    }

    return await response.json();
  },

  async deleteSession(actorId, sessionId) {
    const { API_GATEWAY_URL, DEV_MODE } = window.CONFIG;
    const token = authService.getToken();

    const headers = { 'Content-Type': 'application/json' };
    if (!DEV_MODE) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_GATEWAY_URL}/conversationhistory`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        operation: 'delete_session',
        parameters: {
          actor_id: actorId,
          session_id: sessionId
        }
      })
    });

    if (!response.ok) {
      this._handleUnauthorized(response);
      throw new Error(`Failed to delete session: ${response.status}`);
    }

    return await response.json();
  },

  async listSessionsMetadata(userId) {
    const { API_GATEWAY_URL, DEV_MODE } = window.CONFIG;
    const token = authService.getToken();

    const headers = { 'Content-Type': 'application/json' };
    if (!DEV_MODE) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_GATEWAY_URL}/conversationhistory`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        operation: 'list_sessions_metadata',
        parameters: { actor_id: userId }
      })
    });

    if (!response.ok) {
      this._handleUnauthorized(response);
      throw new Error(`Failed to list sessions metadata: ${response.status}`);
    }

    return await response.json();
  }
};

export default bedrockService;
